import type { CurrentUser } from '@xphoto/shared';
import { useAuth } from './AuthProvider';

/**
 * L'utilisateur a-t-il l'accès ÉCRITURE à la bibliothèque ? Source UNIQUE de vérité
 * côté SPA, alignée mot pour mot sur la règle d'or du serveur (`AccessControl`,
 * `apps/api/src/auth/access.ts`) :
 *
 *   écriture = superadmin  OU  rôle `admin` (écriture globale, équivalent owner)
 *              OU  propriétaire d'une bibliothèque (`libraryId` non nul).
 *
 * Un simple bénéficiaire de lien d'accès (rôle `user`, sans `libraryId`) est en
 * LECTURE SEULE : toutes les routes de mutation lui renvoient 403. L'UI doit donc
 * MASQUER (pas seulement désactiver) les affordances d'écriture pour lui.
 *
 * ⚠ Historiquement, `GalleryView` calculait `superadmin || libraryId` et OUBLIAIT
 * le rôle `admin` (un admin arrivé par lien d'accès, sans bibliothèque propre, était
 * à tort traité en lecture seule). Ce helper corrige et centralise le calcul —
 * ne PAS redupliquer la formule ailleurs, importer ce helper.
 *
 * NB : la NOTATION (★) reste ouverte aux lecteurs (choix produit « collaboratif
 * assumé » : la note est un champ partagé de l'asset) et n'est donc PAS gardée par
 * `canWrite` — voir `RatingControl` / l'action « Noter la sélection ».
 */
export function canWriteFor(user: CurrentUser | null): boolean {
  if (user === null) return false;
  return user.role === 'superadmin' || user.role === 'admin' || (user.libraryId ?? null) !== null;
}

/** Variante hook : lit l'utilisateur actif du contexte d'auth. */
export function useCanWrite(): boolean {
  const { user } = useAuth();
  return canWriteFor(user);
}
